import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { DataService } from 'src/services/data-service';

interface ReceiptGroupDetail {
  id: string;
  refNumber: string;
  description: string;
  groupDate: string;
  receiptCount: number;
  totalAmount: number;
  receipts: Receipt[];
}

interface Receipt {
  id: string;
  active: boolean;
  amount: number;
  currency: string;
  paidAt: string;
  method: string;
  reference: string;
  paymentType: string;
}

interface FeeType {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

interface Currency {
  id: string;
  name: string;
  code: string;
  active: boolean;
}

interface PaymentType {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

enum PaymentMethod {
  Cash = 0,
  CreditCard = 1,
  DebitCard = 2,
  BankTransfer = 3,
  Check = 4,
  MobilePayment = 5,
  Other = 6
}

const PaymentMethodLabels: Record<PaymentMethod, string> = {
  [PaymentMethod.Cash]: 'Cash',
  [PaymentMethod.CreditCard]: 'Credit Card',
  [PaymentMethod.DebitCard]: 'Debit Card',
  [PaymentMethod.BankTransfer]: 'Bank Transfer',
  [PaymentMethod.Check]: 'Check',
  [PaymentMethod.MobilePayment]: 'Mobile Payment',
  [PaymentMethod.Other]: 'Other'
};

interface NewPaymentItem {
  feeTypeId: string;
  currencyId: string;
  paymentTypeId: string;
  amount: number;
  method: PaymentMethod;
  paidAt: string;
}

interface UpdateReceiptGroupDialogProps {
  open: boolean;
  onClose: () => void;
  receiptGroup: ReceiptGroupDetail;
  onSuccess: () => void;
}

const UpdateReceiptGroupDialog: React.FC<UpdateReceiptGroupDialogProps> = ({
  open,
  onClose,
  receiptGroup,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [feeTypesLoading, setFeeTypesLoading] = useState(false);
  const [currenciesLoading, setCurrenciesLoading] = useState(false);
  const [paymentTypesLoading, setPaymentTypesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [feeTypes, setFeeTypes] = useState<FeeType[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([]);

  // Track existing receipts and which ones to delete
  const [existingReceipts, setExistingReceipts] = useState<Receipt[]>([]);
  const [receiptIdsToDelete, setReceiptIdsToDelete] = useState<string[]>([]);

  // Track new payments to add
  const [newPayments, setNewPayments] = useState<NewPaymentItem[]>([]);

  useEffect(() => {
    if (open) {
      fetchFeeTypes();
      fetchCurrencies();
      fetchPaymentTypes();
      // Initialize existing receipts from the receipt group
      setExistingReceipts(receiptGroup.receipts);
      setReceiptIdsToDelete([]);
      setNewPayments([]);
    }
  }, [open, receiptGroup]);

  const fetchFeeTypes = async () => {
    setFeeTypesLoading(true);
    try {
      const response = await DataService.get('api/fee-types/all');

      if (response.ok) {
        const responseData = await response.json();
        let typesList: FeeType[] = [];

        if (responseData.success && responseData.data) {
          typesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          typesList = responseData;
        }

        // Filter only active fee types
        setFeeTypes(typesList.filter((type) => type.active));
      } else {
        throw new Error('Failed to fetch fee types');
      }
    } catch (error) {
      console.error('Error fetching fee types:', error);
      setError('Failed to load fee types. Please try again.');
    } finally {
      setFeeTypesLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    setCurrenciesLoading(true);
    try {
      const response = await DataService.get('api/Currency/all');

      if (response.ok) {
        const responseData = await response.json();
        let currenciesList: Currency[] = [];

        if (responseData.success && responseData.data) {
          currenciesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          currenciesList = responseData;
        }

        // Filter only active currencies
        setCurrencies(currenciesList.filter((currency) => currency.active));
      } else {
        throw new Error('Failed to fetch currencies');
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
      setError('Failed to load currencies. Please try again.');
    } finally {
      setCurrenciesLoading(false);
    }
  };

  const fetchPaymentTypes = async () => {
    setPaymentTypesLoading(true);
    try {
      const response = await DataService.get('api/payment-types/all');

      if (response.ok) {
        const responseData = await response.json();
        let typesList: PaymentType[] = [];

        if (responseData.success && responseData.data) {
          typesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          typesList = responseData;
        }

        // Filter only active payment types
        setPaymentTypes(typesList.filter((type) => type.active));
      } else {
        throw new Error('Failed to fetch payment types');
      }
    } catch (error) {
      console.error('Error fetching payment types:', error);
      setError('Failed to load payment types. Please try again.');
    } finally {
      setPaymentTypesLoading(false);
    }
  };

  const handleRemoveExistingReceipt = (receiptId: string) => {
    // Add to delete list
    setReceiptIdsToDelete([...receiptIdsToDelete, receiptId]);
    // Remove from display list
    setExistingReceipts(existingReceipts.filter((r) => r.id !== receiptId));
  };

  const handleNewPaymentChange = (
    index: number,
    field: keyof NewPaymentItem,
    value: string | number
  ) => {
    const updatedPayments = [...newPayments];
    updatedPayments[index] = {
      ...updatedPayments[index],
      [field]: value
    };
    setNewPayments(updatedPayments);
  };

  const handleAddNewPayment = () => {
    setNewPayments([
      ...newPayments,
      {
        feeTypeId: '',
        currencyId: '',
        paymentTypeId: '',
        amount: 0,
        method: PaymentMethod.Cash,
        paidAt: new Date().toISOString().slice(0, 16)
      }
    ]);
  };

  const handleRemoveNewPayment = (index: number) => {
    const updatedPayments = newPayments.filter((_, i) => i !== index);
    setNewPayments(updatedPayments);
  };

  const validateForm = (): boolean => {
    // Check if there are any changes
    if (receiptIdsToDelete.length === 0 && newPayments.length === 0) {
      setError('No changes detected. Please add or remove receipts.');
      return false;
    }

    // Validate each new payment
    for (let i = 0; i < newPayments.length; i++) {
      const item = newPayments[i];

      if (!item.feeTypeId) {
        setError(`Please select a fee type for new payment #${i + 1}.`);
        return false;
      }

      if (!item.currencyId) {
        setError(`Please select a currency for new payment #${i + 1}.`);
        return false;
      }

      if (!item.paymentTypeId) {
        setError(`Please select a payment type for new payment #${i + 1}.`);
        return false;
      }

      if (!item.amount || item.amount <= 0) {
        setError(`Please enter a valid amount for new payment #${i + 1}.`);
        return false;
      }

      if (!item.paidAt) {
        setError(`Please select a payment date for new payment #${i + 1}.`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setError(null);
    setLoading(true);

    try {
      // Prepare payload
      const payload = {
        receiptGroupId: receiptGroup.id,
        receiptIdsToDelete: receiptIdsToDelete,
        newPayments: newPayments.map((item) => ({
          currencyId: item.currencyId,
          paymentTypeId: item.paymentTypeId,
          feeTypeId: item.feeTypeId,
          amount: item.amount,
          paidAt: new Date(item.paidAt).toISOString(),
          method: item.method
        }))
      };

      const response = await DataService.post(
        'api/ReceiptGroup/update-receipts',
        payload
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.Message || errorData.message || 'Failed to update receipt group'
        );
      }

      const responseData = await response.json();

      // Check if update was successful (handle both uppercase and lowercase)
      const isSuccess =
        responseData.Success !== undefined
          ? responseData.Success
          : responseData.success;
      if (!isSuccess) {
        throw new Error(
          responseData.Message ||
            responseData.message ||
            'Failed to update receipt group'
        );
      }

      // Success
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error updating receipt group:', error);
      setError(
        error instanceof Error
          ? error.message
          : 'Failed to update receipt group. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setExistingReceipts(receiptGroup.receipts);
      setReceiptIdsToDelete([]);
      setNewPayments([]);
      onClose();
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentMethodColor = (method: string): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'info' => {
    const colors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'info'> = {
      'Cash': 'success',
      'CreditCard': 'primary',
      'DebitCard': 'info',
      'BankTransfer': 'secondary',
      'Check': 'warning',
      'MobilePayment': 'primary',
      'Other': 'default'
    };
    return colors[method] || 'default';
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          fontWeight: 'bold',
          py: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon />
          Update Receipt Group - {receiptGroup.refNumber}
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          pt: 3,
          pb: 2,
          minHeight: '400px',
          maxHeight: '70vh',
          overflowY: 'auto'
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Receipt Group Information */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            color="primary.main"
            sx={{ mb: 2 }}
          >
            Receipt Group Information
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reference Number"
                value={receiptGroup.refNumber}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <ReceiptIcon color="primary" sx={{ mr: 1 }} />
                  )
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Receipts"
                value={`${existingReceipts.length} receipts`}
                InputProps={{
                  readOnly: true
                }}
                size="small"
              />
            </Grid>
          </Grid>
        </Paper>

        <Divider sx={{ mb: 2 }} />

        {/* Existing Receipts Section */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            color="primary.main"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
          >
            <ReceiptIcon />
            Existing Receipts ({existingReceipts.length})
          </Typography>

          {existingReceipts.length === 0 ? (
            <Alert severity="info">
              All receipts have been removed. Add new receipts below or cancel to keep existing receipts.
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: '250px', overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Method</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Payment Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100', textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {existingReceipts.map((receipt, index) => (
                    <TableRow key={receipt.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600" color="primary">
                          {receipt.reference}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <AttachMoneyIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          <Typography variant="body2" fontWeight="600" color="success.main">
                            {receipt.currency} {receipt.amount.toLocaleString()}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={receipt.method}
                          size="small"
                          color={getPaymentMethodColor(receipt.method)}
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(receipt.paidAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveExistingReceipt(receipt.id)}
                          title="Remove this receipt"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* New Payments Section */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              color="primary.main"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <AddIcon />
              New Payments ({newPayments.length})
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNewPayment}
              size="small"
              disabled={
                feeTypesLoading || currenciesLoading || paymentTypesLoading
              }
            >
              Add Payment
            </Button>
          </Box>

          {feeTypesLoading || currenciesLoading || paymentTypesLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '150px'
              }}
            >
              <CircularProgress />
            </Box>
          ) : newPayments.length === 0 ? (
            <Alert severity="info">
              No new payments added yet. Click "Add Payment" to add payment items.
            </Alert>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ maxHeight: 'calc(70vh - 350px)', overflowY: 'auto' }}
            >
              <Table
                size="small"
                sx={{ '& .MuiTableCell-root': { py: 1, px: 1 } }}
                stickyHeader
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold', width: '3%', bgcolor: 'grey.100' }}>
                      #
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '18%', bgcolor: 'grey.100' }}>
                      Fee Type
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '10%', bgcolor: 'grey.100' }}>
                      Currency
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '18%', bgcolor: 'grey.100' }}>
                      Payment Type
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '12%', bgcolor: 'grey.100' }}>
                      Method
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '12%', bgcolor: 'grey.100' }}>
                      Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '17%', bgcolor: 'grey.100' }}>
                      Payment Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '5%', textAlign: 'center', bgcolor: 'grey.100' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {newPayments.map((payment, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small" required>
                          <Select
                            value={payment.feeTypeId}
                            onChange={(e) =>
                              handleNewPaymentChange(
                                index,
                                'feeTypeId',
                                e.target.value
                              )
                            }
                            displayEmpty
                          >
                            <MenuItem value="" disabled>
                              Select Fee Type
                            </MenuItem>
                            {feeTypes.map((type) => (
                              <MenuItem key={type.id} value={type.id}>
                                {type.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small" required>
                          <Select
                            value={payment.currencyId}
                            onChange={(e) =>
                              handleNewPaymentChange(
                                index,
                                'currencyId',
                                e.target.value
                              )
                            }
                            displayEmpty
                          >
                            <MenuItem value="" disabled>
                              Select Currency
                            </MenuItem>
                            {currencies.map((currency) => (
                              <MenuItem key={currency.id} value={currency.id}>
                                {currency.code}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small" required>
                          <Select
                            value={payment.paymentTypeId}
                            onChange={(e) =>
                              handleNewPaymentChange(
                                index,
                                'paymentTypeId',
                                e.target.value
                              )
                            }
                            displayEmpty
                          >
                            <MenuItem value="" disabled>
                              Select Payment Type
                            </MenuItem>
                            {paymentTypes.map((type) => (
                              <MenuItem key={type.id} value={type.id}>
                                {type.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small" required>
                          <Select
                            value={payment.method}
                            onChange={(e) =>
                              handleNewPaymentChange(
                                index,
                                'method',
                                parseInt(e.target.value as string)
                              )
                            }
                          >
                            {Object.entries(PaymentMethodLabels).map(
                              ([value, label]) => (
                                <MenuItem key={value} value={parseInt(value)}>
                                  {label}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={payment.amount || ''}
                          onChange={(e) =>
                            handleNewPaymentChange(
                              index,
                              'amount',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          placeholder="0.00"
                          inputProps={{ min: 0, step: 0.01 }}
                          required
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="datetime-local"
                          value={payment.paidAt}
                          onChange={(e) =>
                            handleNewPaymentChange(
                              index,
                              'paidAt',
                              e.target.value
                            )
                          }
                          InputLabelProps={{
                            shrink: true
                          }}
                          required
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveNewPayment(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          size="small"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          size="small"
          disabled={
            loading ||
            feeTypesLoading ||
            currenciesLoading ||
            paymentTypesLoading ||
            (receiptIdsToDelete.length === 0 && newPayments.length === 0)
          }
          startIcon={
            loading ? (
              <CircularProgress size={20} />
            ) : (
              <CheckCircleIcon />
            )
          }
        >
          {loading ? 'Updating...' : 'Update Receipt Group'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateReceiptGroupDialog;
